import * as d3 from "d3";
import _ from "lodash";
import moment from "moment";
import datetimeManager from "../managers/datetime";
import mathManager from "../managers/math";
import commonRenderer from "../renderers/common";
import {
  CHART_CTT,
  CHART_LAYER_BID,
  CHART_LAYER_ASK,
  CHART_LAYER_TREND_LINES,
  API_KEYS_CTT,
  API_KEYS_SPREAD,
  CHART_POINT_RADIUS,
  CHART_SPREAD
} from "../managers/constants";
import { CHART_LABEL_TIME, CHART_LABEL_COST_TICKS, CHART_LABEL_SPREAD_TICKS } from "../managers/string-literals";

const service = (() => {
  const render = (params) => {
    // View / hide layers on the drawing
    let showTrendLinesLayer = _.includes(params.selectedLayers, CHART_LAYER_TREND_LINES) ? true : false;
    let showAskPointsLayer = _.includes(params.selectedLayers, CHART_LAYER_ASK) ? true : false;
    let showBidPointsLayer = _.includes(params.selectedLayers, CHART_LAYER_BID) ? true : false;

    // Unique class for drag movements across X-axis
    let uniqueLineClass = "line" + params.uniqueKey;
    let uniqueTrendLineAskClass = "trendline-ask-" + params.uniqueKey;
    let uniqueTrendLineBidClass = "trendline-bid-" + params.uniqueKey;
    let uniquePointAskClass = "point-ask-" + params.uniqueKey;
    let uniquePointBidClass = "point-bid-" + params.uniqueKey;

    // Dimensions
    let margin = params.dimensions.margin,
      width = params.dimensions.width,
      height = params.dimensions.height;

    let yAxisBidTooltip = null;
    let yAxisAskTooltip = null;
    let yAxisTooltip = null;

    let lineCurveAlpha = 0.5; // Curvature of lines

    // Positioning for labels on axis
    let lineStrokeWidth = 1;

    // Positioning for tick labels on axis
    let yAxisLabel = null;
    let dualYAxisLabel = null;
    let yDomain = null;
    let dualYDomain = null;

    let xAxisLabel = CHART_LABEL_TIME;
    if (params.primaryChart === CHART_CTT) {
      yAxisLabel = CHART_LABEL_COST_TICKS;
      dualYAxisLabel = CHART_LABEL_SPREAD_TICKS;
    } else {
      yAxisLabel = CHART_LABEL_SPREAD_TICKS;
      dualYAxisLabel = CHART_LABEL_COST_TICKS;
    }

    //To show the scale of only visible layers.
    let yAxisScaleRangeProperties = [];
    if ((showAskPointsLayer && showBidPointsLayer) || (!showAskPointsLayer && !showBidPointsLayer)) {
      yAxisScaleRangeProperties = [API_KEYS_CTT.ask, API_KEYS_CTT.bid];
    } else if (showAskPointsLayer) {
      yAxisScaleRangeProperties = [API_KEYS_CTT.ask];
    } else if (showBidPointsLayer) {
      yAxisScaleRangeProperties = [API_KEYS_CTT.bid];
    }

    // ==================================================
    // CREATE SVG
    // ==================================================

    let svg = commonRenderer.createSvg(params.drawingContainerElementId, width, height, margin);

    // ==================================================
    // CREATE SCALE
    // ==================================================

    // Time scale for X axis
    let x = d3
      .scaleTime()
      .range([1, width])
      .domain(
        d3.extent(params.data, function(d) {
          return datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp], params.zoneOffset);
        })
      );

    let getScaleForCost = (cost) => {
      if (params.primaryChart === CHART_CTT) {
        return y(cost);
      } else {
        return dualY(cost);
      }
    };
    let getScaleForSpread = (spread) => {
      if (params.primaryChart === CHART_CTT) {
        return dualY(spread);
      } else {
        return y(spread);
      }
    };

    let drawBidAskAxisTooltip = (id) => {
      if (params.primaryChart === CHART_CTT) {
        return commonRenderer.drawChartTooltipYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      } else {
        return commonRenderer.drawChartTooltipDualYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      }
    };
    let drawTopOfTheBookAxisTooltip = (id) => {
      if (params.primaryChart === CHART_CTT) {
        return commonRenderer.drawChartTooltipDualYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      } else {
        return commonRenderer.drawChartTooltipYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      }
    };

    //returns Y and dualY Domain.
    if (params.primaryChart === CHART_CTT) {
      yDomain = commonRenderer.getMinMaxValues(params.data, yAxisScaleRangeProperties);
      dualYDomain = commonRenderer.getMinMaxValues(params.data, API_KEYS_SPREAD.l1);
    } else {
      yDomain = commonRenderer.getMinMaxValues(params.data, API_KEYS_SPREAD.l1);
      dualYDomain = commonRenderer.getMinMaxValues(params.data, yAxisScaleRangeProperties);
    }

    // Linear scale for Y axis
    let y = d3
      .scaleLinear()
      .range([height, 1])
      .domain(yDomain)
      .nice();

    // Linear scale for Y-Dual axis
    let dualY = d3
      .scaleLinear()
      .range([height, 1])
      .domain(dualYDomain)
      .nice();

    // ==================================================
    // FIND MIN & MAX VALUES
    // ==================================================

    // For X axis
    var xDomain = d3.extent(params.data, function(d) {
      return datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp], params.zoneOffset);
    });

    // ==================================================
    // CREATE AXES
    // ==================================================

    // Create X axis
    let xAxis = commonRenderer.createXAxisTimeScale(
      x,
      commonRenderer.generateXAxisTickValues(params.data, params.zoneOffset, params.granularityCode, width),
      params.tickFormat
    );

    // Create Y axis
    let yAxis = commonRenderer.createYAxisLinearScale(y);

    // Create Y-Dual axis
    let dualYAxis = commonRenderer.createDualYAxisLinearScale(dualY);

    // ==================================================
    // DRAW AXES
    // ==================================================

    // Draw X axis
    commonRenderer.drawXAxis(svg, height, xAxis);

    // Draw Y axis
    commonRenderer.drawYAxis(svg, yAxis);

    // Draw Y-Dual axis
    commonRenderer.drawDualYAxis(svg, width, dualYAxis);

    // ==================================================
    // DRAW LABEL on AXES
    // ==================================================

    // Label for X axis
    commonRenderer.drawXAxisLabel(svg, width, height, xAxisLabel);

    // Label for Y axis
    commonRenderer.drawYAxisLabel(svg, height, yAxisLabel, margin);

    // Label for Y-Dual axis
    commonRenderer.drawYDualAxisLabel(svg, width, height, dualYAxisLabel, margin);

    // ==================================================
    // DRAW COMMON TOOLTIP
    // ==================================================
    let tooltip = commonRenderer.drawChartDataTooltip(params.drawingContainerElementId, params.uniqueKey);

    // ==================================================
    // DRAW TOOLTIP FOR BID FOCUS LINE.
    // ==================================================
    //tooltip on y axis for BID to follow focus line
    if (showBidPointsLayer) {
      yAxisBidTooltip = drawBidAskAxisTooltip("yAxisBidTooltip");
    }

    // ==================================================
    // DRAW TOOLTIP FOR ASK FOCUS LINE.
    // ==================================================
    //tooltip on y axis for ASK to follow focus line
    if (showAskPointsLayer) {
      yAxisAskTooltip = drawBidAskAxisTooltip("yAxisAskTooltip");
    }

    // ==================================================
    // DRAW TOOLTIP FOR TOP OF THE BOOK FOCUS LINE.
    // ==================================================
    //tooltip on y axis to follow focus line
    yAxisTooltip = drawTopOfTheBookAxisTooltip("yAxisTooltip");

    // ==================================================
    // HIGHLIGH ROLE SHIFT, ADD ALTERNATE BACKGROUND COLORS.
    // ==================================================
    commonRenderer.highlightRoleShift(params.data, params.zoneOffset, API_KEYS_CTT, svg, height, x);

    // ==================================================
    // TREND LINES
    // ==================================================
    let trendLineAsk = null;
    let trendLineBid = null;
    if (showTrendLinesLayer) {
      let xLabels = params.data.map(function(d) {
        return datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp], params.zoneOffset);
      });

      // get the x and y values for least squares
      let xSeriesAsk = d3.range(1, xLabels.length + 1);
      let ySeriesAsk = params.data.map(function(d) {
        return d[API_KEYS_CTT.ask];
      });

      let leastSquaresAsk = commonRenderer.leastSquares(xSeriesAsk, ySeriesAsk);

      let xSeriesBid = d3.range(1, xLabels.length + 1);
      let ySeriesBid = params.data.map(function(d) {
        return d[API_KEYS_CTT.bid];
      });

      let leastSquaresBid = commonRenderer.leastSquares(xSeriesBid, ySeriesBid);

      // apply the reults of the least squares regression
      let x1 = xLabels[0];
      let x2 = xLabels[xLabels.length - 1];

      let y1Ask = leastSquaresAsk[0] + leastSquaresAsk[1];
      let y2Ask = leastSquaresAsk[0] * xSeriesAsk.length + leastSquaresAsk[1];
      let trendDataAsk = [[x1, y1Ask, x2, y2Ask]];

      if (showAskPointsLayer) {
        trendLineAsk = svg.selectAll("." + uniqueTrendLineAskClass).data(trendDataAsk);
        trendLineAsk
          .enter()
          .append("line")
          .attr("class", "line trendline trendline-ask " + uniqueTrendLineAskClass)
          .attr("x1", function(d) {
            return x(d[0]);
          })
          .attr("y1", function(d) {
            return getScaleForCost(d[1]);
          })
          .attr("x2", function(d) {
            return x(d[2]);
          })
          .attr("y2", function(d) {
            return getScaleForCost(d[3]);
          });
      }

      let y1Bid = leastSquaresBid[0] + leastSquaresBid[1];
      let y2Bid = leastSquaresBid[0] * xSeriesBid.length + leastSquaresBid[1];
      let trendDataBid = [[x1, y1Bid, x2, y2Bid]];

      if (showBidPointsLayer) {
        trendLineBid = svg.selectAll("." + uniqueTrendLineBidClass).data(trendDataBid);
        trendLineBid
          .enter()
          .append("line")
          .attr("class", "line trendline trendline-bid " + uniqueTrendLineBidClass)
          .attr("x1", function(d) {
            return x(d[0]);
          })
          .attr("y1", function(d) {
            return getScaleForCost(d[1]);
          })
          .attr("x2", function(d) {
            return x(d[2]);
          })
          .attr("y2", function(d) {
            return getScaleForCost(d[3]);
          });
      }
    }

    // ==================================================
    // ASK POINTS
    // ==================================================

    if (showAskPointsLayer) {
      svg
        .selectAll("." + uniquePointAskClass)
        .data(
          params.data.filter(function(obj) {
            return obj[API_KEYS_CTT.ask] ? true : false;
          })
        )
        .enter()
        .append("circle")
        .attr("class", "scatter-point-ask " + uniquePointAskClass)
        .attr("cx", function(d) {
          return x(datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp], params.zoneOffset));
        })
        .attr("r", CHART_POINT_RADIUS)
        .attr("cy", function(d) {
          return getScaleForCost(d[API_KEYS_CTT.ask]);
        });
    }

    // ==================================================
    // BID POINTS
    // ==================================================

    if (showBidPointsLayer) {
      svg
        .selectAll("." + uniquePointBidClass)
        .data(
          params.data.filter(function(obj) {
            return obj[API_KEYS_CTT.bid] ? true : false;
          })
        )
        .enter()
        .append("circle")
        .attr("class", "scatter-point-bid " + uniquePointBidClass)
        .attr("cx", function(d) {
          return x(datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp], params.zoneOffset));
        })
        .attr("r", CHART_POINT_RADIUS)
        .attr("cy", function(d) {
          return getScaleForCost(d[API_KEYS_CTT.bid]);
        });
    }

    // ==================================================
    // TOP OF THE BOOK SPREAD LINE
    // ==================================================
    let spreadLevel = d3
      .line()
      .x(function(d) {
        return x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset));
      })
      .y(function(d) {
        return getScaleForSpread(d[API_KEYS_SPREAD["l1"]]);
      })
      .curve(d3.curveCatmullRom.alpha(lineCurveAlpha));

    //Drawing spread Line.
    svg
      .append("path")
      .datum(
        params.data.filter(function(obj) {
          return obj[API_KEYS_SPREAD["l1"]] ? true : false;
        })
      )
      .attr("class", "line spread-line-level1-overlay " + uniqueLineClass)
      .attr("stroke-width", lineStrokeWidth)
      .attr("d", spreadLevel);

    // ==================================================
    // FOCUS LINES
    // ==================================================

    var focus = svg.append("g").style("display", "none");

    //Creating X axis focus line   //createFocusLine(focus, params, id);
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineX");

    //Creating Y axis Ask Focus line
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYAsk");

    //Creating Y axis Bid Focus Line.
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYBid");

    //top of the book spread Focus Line.
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYTopOfTheBook");

    //Create Ask focus circle for point highlighting   //createFocusCircle(focus, params, id, class);
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleAsk", "focus-point-ask");

    //Create Bid  focus circle for point highlighting
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleBid", "focus-point-bid");

    //Create Top of the book focus circle for point highlighting
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleTopOfTheBook", "focus-point-spread-overlay");

    var bisectDate = d3.bisector(function(d) {
      return d[API_KEYS_CTT.timestamp];
    }).left;

    // Drawing RECT in SVG to track mouse position
    svg
      .append("rect")
      .attr("class", "overlay")
      .attr("width", width)
      .attr("height", height)
      .on("mouseover", function() {
        focus.style("display", null);
      })
      .on("mouseout", function() {
        tooltip.style("display", "none");
        if (showBidPointsLayer) {
          yAxisBidTooltip.style("display", "none");
        }
        if (showAskPointsLayer) {
          yAxisAskTooltip.style("display", "none");
        }
        yAxisTooltip.style("display", "none");
        focus.style("display", "none");
      })
      .on("mousemove", function() {
        // Get coordinates of current mouse position
        var mouse = d3.mouse(this);
        var mouseDate = x.invert(mouse[0]);

        // Return the index of the current data item
        var i = bisectDate(params.data, moment(mouseDate).unix());
        i = i >= params.data.length ? params.data.length - 1 : i;

        var d0 = params.data[i - 1]; // Previous point
        var d1 = params.data[i]; // Current point

        // Used to calculate difference between current data points and previous data points
        let previousDataObject = undefined;

        // Compute which date value is closest to the mouse pointer
        if (d0 !== undefined) {
          if (moment(mouseDate).unix() - d0[API_KEYS_CTT.timestamp] > d1[API_KEYS_CTT.timestamp] - moment(mouseDate).unix()) {
            var d = d1;
            previousDataObject = d0;
          } else {
            var d = d0;
            previousDataObject = params.data[i - 2];
          }
        } else {
          var d = d1;
        }

        // Get X value of the current mouse position
        var hoveredXValue = x(datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp], params.zoneOffset)); //getting x value of current mouse position

        // ==================================================
        // DRAW COMMON TOOLTIP
        // ==================================================

        let tooltipContent =
          "<tr><td colspan=3>" +
          datetimeManager.getWholeDateTime(d[API_KEYS_CTT.timestamp], params.zoneOffset, commonRenderer.getXAxisDateFormat(params.granularityCode)) +
          " " +
          params.zoneName +
          " (" +
          params.zoneTiming +
          ")" +
          "</td></tr>";

        tooltipContent += "<tr><td>Contract:</td><td>" + d[API_KEYS_CTT.contract] + "</td></tr>";
        const inversePercentageIndication = true;

        if (showBidPointsLayer) {
          tooltipContent +=
            "<tr><td>Buy:</td><td>" +
            mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(d[API_KEYS_CTT.bid], CHART_CTT)) +
            "</td><td>" +
            commonRenderer.getPercentageChange(
              previousDataObject ? previousDataObject[API_KEYS_CTT.bid] : undefined,
              d[API_KEYS_CTT.bid],
              params.granularityCode,
              inversePercentageIndication
            ) +
            "</td></tr>";

          tooltipContent += "<tr><td>Buy Depth:</td><td>" + 
          mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(d[API_KEYS_CTT.bidDepth], CHART_CTT))
          + " Level</td></tr>";
        }

        if (showAskPointsLayer) {
          tooltipContent +=
            "<tr><td>Sell:</td><td>" +
            mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(d[API_KEYS_CTT.ask], CHART_CTT)) +
            "</td><td>" +
            commonRenderer.getPercentageChange(
              previousDataObject ? previousDataObject[API_KEYS_CTT.ask] : undefined,
              d[API_KEYS_CTT.ask],
              params.granularityCode,
              inversePercentageIndication
            ) +
            "</td></tr>";

          tooltipContent += "<tr><td>Sell Depth:</td><td>" +
          mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(d[API_KEYS_CTT.askDepth], CHART_CTT)) +
          " Level</td></tr>";
        }

        tooltipContent +=
          "<tr><td>Top Of The Book:</td><td>" +
          mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(d[API_KEYS_SPREAD.l1], CHART_SPREAD)) +
          "</td><td>" +
          commonRenderer.getPercentageChange(
            previousDataObject ? previousDataObject[API_KEYS_SPREAD.l1] : undefined,
            d[API_KEYS_SPREAD.l1],
            params.granularityCode,
            inversePercentageIndication
          ) +
          "</td></tr>";

        //mouse[0] is the x co-ordinate of current mouse position.
        commonRenderer.positionChartDataTooltip(width / 2, mouse[0], tooltip, tooltipContent, margin);

        // ==================================================
        // POSITION X AXIS (VERTICAL) FOCUS LINE.
        // ==================================================

        commonRenderer.positionXAxisFocusLine(focus, params.uniqueKey, "focusLineX", hoveredXValue, height);

        // =============================================================================
        // POSITION ASK FOCUS LINE & TOOLTIP ON IT AND POSITION ASK FOCUS CIRCLE (HOVERED POINT)
        // =============================================================================

        if (showAskPointsLayer && d[API_KEYS_CTT.ask]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYAsk",
            x(xDomain[0]),
            getScaleForCost(d[API_KEYS_CTT.ask]),
            x(xDomain[1]),
            getScaleForCost(d[API_KEYS_CTT.ask])
          );

          commonRenderer.positionChartTooltipAxis(yAxisAskTooltip, d[API_KEYS_CTT.ask], getScaleForCost(d[API_KEYS_CTT.ask]), CHART_CTT);

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleAsk",
            x(datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp])),
            getScaleForCost(d[API_KEYS_CTT.ask])
          );
        } else {
          focus.select("#focusLineYAsk" + params.uniqueKey).style("display", "none");
          if (showAskPointsLayer) {
            yAxisAskTooltip.style("display", "none");
          }
          focus.select("#focusCircleAsk" + params.uniqueKey).style("display", "none");
        }
        // =============================================================================
        // POSITION BID FOCUS LINE & TOOLTIP ON IT AND POSITION BID FOCUS CIRCLE (HOVERED POINT)
        // =============================================================================

        if (showBidPointsLayer && d[API_KEYS_CTT.bid]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYBid",
            x(xDomain[0]),
            getScaleForCost(d[API_KEYS_CTT.bid]),
            x(xDomain[1]),
            getScaleForCost(d[API_KEYS_CTT.bid])
          );

          commonRenderer.positionChartTooltipAxis(yAxisBidTooltip, d[API_KEYS_CTT.bid], getScaleForCost(d[API_KEYS_CTT.bid]), CHART_CTT);

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleBid",
            x(datetimeManager.getMomentObject(d[API_KEYS_CTT.timestamp])),
            getScaleForCost(d[API_KEYS_CTT.bid])
          );
        } else {
          focus.select("#focusLineYBid" + params.uniqueKey).style("display", "none");
          if (showBidPointsLayer) {
            yAxisBidTooltip.style("display", "none");
          }
          focus.select("#focusCircleBid" + params.uniqueKey).style("display", "none");
        }

        // =====================================================================================================
        // POSITION TOP OF THE BOOK FOCUS LINE & TOOLTIP ON IT AND POSITION TOP OF THE BOOK FOCUS CIRCLE (HOVERED POINT)
        // =====================================================================================================
        if (d[API_KEYS_SPREAD.l1]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYTopOfTheBook",
            x(xDomain[0]),
            getScaleForSpread(d[API_KEYS_SPREAD.l1]),
            x(xDomain[1]),
            getScaleForSpread(d[API_KEYS_SPREAD.l1])
          );

          commonRenderer.positionChartTooltipAxis(yAxisTooltip, d[API_KEYS_SPREAD.l1], getScaleForSpread(d[API_KEYS_SPREAD.l1]), CHART_SPREAD);

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleTopOfTheBook",
            x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset)),
            getScaleForSpread(d[API_KEYS_SPREAD.l1])
          );
        } else {
          focus.select("#focusLineYTopOfTheBook" + params.uniqueKey).style("display", "none");
          yAxisTooltip.style("display", "none");
          focus.select("#focusCircleTopOfTheBook" + params.uniqueKey).style("display", "none");
        }
      });
  };

  return { render };
})();

export default service;



// WEBPACK FOOTER //
// ./src/services/renderers/cost-to-trade-scatter-plot-with-spread-line.js